import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import type { MissionSignatureStatus } from '../../utils/missionSignatureApi';
import type { ActionFeedback, SignatureDocumentKind } from '../../hooks/useMissionSignature';

type MissionSignaturePanelProps = {
  status: MissionSignatureStatus | null;
  loading: boolean;
  error: string;
  connectionsPath: string;
  pollingTimedOut: boolean;
  syncing: boolean;
  relaunching: boolean;
  relaunchFeedback: ActionFeedback;
  cancelling: boolean;
  cancelFeedback: ActionFeedback;
  downloading: SignatureDocumentKind | null;
  downloadError: string;
  includeCompanySignatory: boolean;
  onToggleCompanySignatory: (include: boolean) => void;
  onSyncNow: () => void;
  onRelaunch: () => void;
  onCancel: () => void;
  onDownload: (kind: SignatureDocumentKind) => void;
  onNavigate: (path: string) => void;
};

// Status line for a signature in flight. While the ceremony runs the mission page polls the status,
// so import + promotion appear automatically (JeSignExpert webhook, then the page poll, then the
// hourly reconcile as a backstop). The panel reports progress. While the collecte is in progress
// (JeSignExpert `inProgress` or `pending`) it always offers "Vérifier maintenant" (POST /sync),
// "Relancer le signataire" (POST /relaunch) and "Annuler la signature" (POST /reset, confirmed in
// the panel first); once JeSignExpert is finished, "Vérifier maintenant" only appears when polling
// gave up before the import landed. Every mutation control (those three, the co-signatory choice)
// needs `can_mutate`: reading this status only
// takes canReadMission, the routes behind them canMutateMission. A signed mission's report and
// attestation are downloaded from the report's actions column; only a partially signed collecte,
// which leaves the mission unsigned, offers its documents here; its next collecte is launched by
// the mission page's single Signer button, like the first one.
const progressMessage = (step: MissionSignatureStatus['step']): string => {
  switch (step) {
    case 'in_progress':
      return __('Signature en cours dans JeSignExpert.');
    case 'import_pending':
    case 'complete':
      return __('Signature effectuée : finalisation automatique en cours.');
    default:
      return '';
  }
};

const MissionSignaturePanel = ({
  status,
  loading,
  error,
  connectionsPath,
  pollingTimedOut,
  syncing,
  relaunching,
  relaunchFeedback,
  cancelling,
  cancelFeedback,
  downloading,
  downloadError,
  includeCompanySignatory,
  onToggleCompanySignatory,
  onSyncNow,
  onRelaunch,
  onCancel,
  onDownload,
  onNavigate,
}: MissionSignaturePanelProps) => {
  // Cancelling is irreversible for the signatories, so it asks once in the panel. Focus goes to
  // "keep" when the question opens and back to the trigger when it is dismissed; a step change
  // (signed meanwhile, cancelled) closes it.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancelTriggerRef = useRef<HTMLButtonElement>(null);
  const keepCollecteRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  const step = status?.step;

  useEffect(() => {
    setConfirmingCancel(false);
  }, [step]);

  useEffect(() => {
    if (confirmingCancel) {
      keepCollecteRef.current?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      cancelTriggerRef.current?.focus();
    }
  }, [confirmingCancel]);

  if (loading && status === null) {
    return <p>{__('Chargement de la signature électronique...')}</p>;
  }

  if (status === null) {
    return error !== '' ? (
      <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
        <p>{error}</p>
      </div>
    ) : null;
  }

  if (status.step === 'not_configured') {
    return (
      <p className="mission-signature-panel__hint">
        {__('La signature électronique JeSignExpert n’est pas configurée sur ce serveur.')}
      </p>
    );
  }

  const message = progressMessage(status.step);
  const wasCancelled = status.collection?.state === 'cancelled';
  const canMutate = status.can_mutate === true;
  const collecteInProgress = status.step === 'in_progress';
  const jseFinished = status.step === 'import_pending' || status.step === 'complete';
  const offerSyncNow = canMutate && (collecteInProgress || (pollingTimedOut && jseFinished));
  const partiallySigned = status.step === 'partial';
  const offerSignedDownload = status.signed_download_available
    && typeof status.signed_download_url === 'string' && status.signed_download_url !== '';
  const offerAttestationDownload = status.attestation_download_available
    && typeof status.attestation_download_url === 'string' && status.attestation_download_url !== '';

  return (
    <section className="mission-signature-panel" aria-label={__('Signature électronique')}>
      <p className="mission-signature-panel__provider">
        {__('Signature électronique propulsée par JeSignExpert.')}
      </p>

      {status.step === 'not_connected' ? (
        <div className="mission-signature-panel__notice">
          <p>{__('Connectez JeSignExpert pour signer le rapport de cette mission.')}</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => { onNavigate(connectionsPath); }}
          >
            {__('Ouvrir les connexions')}
          </button>
        </div>
      ) : null}

      {typeof status.connection?.office_name === 'string' && status.connection.office_name !== '' ? (
        <p className="mission-signature-panel__office">
          {__('Cabinet JeSignExpert :')} {status.connection.office_name}
        </p>
      ) : null}

      {status.step !== 'not_connected' && status.can_sign === false ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>
            {__('Ce compte JeSignExpert n’est pas signataire sur le cabinet connecté : il peut préparer une collecte, mais la cérémonie ne pourra pas aboutir. Connectez le cabinet sur lequel vous êtes signataire, ou faites-vous attribuer ce droit.')}
          </p>
        </div>
      ) : null}

      {status.email_pivot_warning !== null && status.email_pivot_warning !== '' ? (
        <div className="accounting-hub-notice" role="status">
          <p>{status.email_pivot_warning}</p>
        </div>
      ) : null}

      {canMutate && status.can_start === true && status.company_signatory?.designated === true ? (
        <div className="mission-signature-panel__cosigner">
          <label htmlFor="mission-signature-cosigner">
            <input
              id="mission-signature-cosigner"
              type="checkbox"
              checked={includeCompanySignatory}
              disabled={status.company_signatory.complete === false}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onToggleCompanySignatory(event.target.checked);
              }}
            />
            {__('Faire signer aussi')} {status.company_signatory.display_name}
          </label>
          {status.company_signatory.complete === false ? (
            <p className="mission-signature-panel__hint">
              {__('Renseignez le mobile de ce signataire pour pouvoir l’inviter.')}
            </p>
          ) : null}
        </div>
      ) : null}

      {message !== '' ? (
        <div className="accounting-hub-notice" role="status">
          <p>{message}</p>
        </div>
      ) : null}

      {partiallySigned ? (
        <div className="mission-signature-panel__notice">
          <div className="accounting-hub-notice" role="status">
            <p><strong>{__('Collecte close — signée partiellement')}</strong></p>
            <p>
              {__('Tous les signataires n’ont pas signé : la mission n’est pas marquée signée. Téléchargez le document partiellement signé ; le bouton « Signer » de la mission lance une nouvelle collecte.')}
            </p>
          </div>
          <div className="mission-signature-panel__actions">
            {offerSignedDownload ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={downloading === 'signed'}
                onClick={() => { onDownload('signed'); }}
              >
                {downloading === 'signed' ? __('Téléchargement...') : __('Document partiellement signé')}
              </button>
            ) : null}
            {offerAttestationDownload ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={downloading === 'attestation'}
                onClick={() => { onDownload('attestation'); }}
              >
                {downloading === 'attestation' ? __('Téléchargement...') : __('Attestation de signature')}
              </button>
            ) : null}
          </div>
          {downloadError !== '' ? (
            <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
              <p>{downloadError}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {wasCancelled && cancelFeedback?.kind !== 'success' ? (
        <div className="accounting-hub-notice" role="status">
          <p>{__('Signature annulée. Vous pouvez la relancer.')}</p>
        </div>
      ) : null}

      {offerSyncNow ? (
        <div className="mission-signature-panel__notice">
          {pollingTimedOut ? (
            <p role="status">{__('La finalisation prend plus de temps que prévu.')}</p>
          ) : null}
          <div className="mission-signature-panel__actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={syncing}
              onClick={onSyncNow}
            >
              {syncing ? __('Vérification...') : __('Vérifier maintenant')}
            </button>
            {collecteInProgress ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={relaunching}
                onClick={onRelaunch}
              >
                {relaunching ? __('Relance en cours...') : __('Relancer le signataire')}
              </button>
            ) : null}
            {collecteInProgress && !confirmingCancel ? (
              <button
                ref={cancelTriggerRef}
                type="button"
                className="btn-secondary"
                disabled={cancelling}
                onClick={() => { setConfirmingCancel(true); }}
              >
                {cancelling ? __('Annulation...') : __('Annuler la signature')}
              </button>
            ) : null}
          </div>
          {collecteInProgress && confirmingCancel ? (
            <div
              className="accounting-hub-notice"
              role="group"
              aria-labelledby="mission-signature-cancel-question"
            >
              <p id="mission-signature-cancel-question">
                <strong>{__('Annuler la collecte ?')}</strong>{' '}
                {__('Les signataires ne pourront plus signer ce document.')}
              </p>
              <div className="mission-signature-panel__actions">
                <button
                  type="button"
                  className="btn-danger"
                  onClick={() => {
                    setConfirmingCancel(false);
                    onCancel();
                  }}
                >
                  {__('Oui, annuler la collecte')}
                </button>
                <button
                  ref={keepCollecteRef}
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    restoreFocusRef.current = true;
                    setConfirmingCancel(false);
                  }}
                >
                  {__('Non, garder la collecte')}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {cancelFeedback?.kind === 'success' ? (
        <div className="accounting-hub-notice accounting-hub-notice-success" role="status">
          <p>{cancelFeedback.message}</p>
          <p>{__('Le bouton « Signer » de la mission lance une nouvelle collecte.')}</p>
        </div>
      ) : null}

      {cancelFeedback?.kind === 'error' ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{cancelFeedback.message}</p>
        </div>
      ) : null}

      {relaunchFeedback?.kind === 'success' ? (
        <div className="accounting-hub-notice accounting-hub-notice-success" role="status">
          <p>{relaunchFeedback.message}</p>
        </div>
      ) : null}

      {relaunchFeedback?.kind === 'error' ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{relaunchFeedback.message}</p>
        </div>
      ) : null}

      {error !== '' ? (
        <div className="accounting-hub-notice accounting-hub-notice-error" role="alert">
          <p>{error}</p>
        </div>
      ) : null}
    </section>
  );
};

export default MissionSignaturePanel;
